import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function LogoDirection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [logoGuidance, setLogoGuidance] = useState(null);
  const [archetypeInfo, setArchetypeInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedLogoDirection, setSavedLogoDirection] = useState(null);

  // Logo upload states
  const [logos, setLogos] = useState({ horizontal: null, vertical: null });

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if user has saved logo direction
    await fetchSavedLogoDirection();

    // Check if user has uploaded logos
    await fetchLogos();

    setLoading(false);
  };

  const fetchSavedLogoDirection = async () => {
    try {
      const response = await fetch('/api/brand/get-saved-logo-direction', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSavedLogoDirection(data.logo_direction);
        setLogoGuidance(data.logo_direction);

        // Get archetype info
        await fetchArchetypeInfo();
      }
      // If 404, no saved logo direction - that's okay
    } catch (err) {
      console.log('No saved logo direction found or error fetching:', err);
    }
  };

  const fetchArchetypeInfo = async () => {
    try {
      const response = await fetch('/api/brand/get-archetypes', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setArchetypeInfo({
          primary: data.primary?.name,
          secondary: data.secondary?.name,
          tertiary: data.tertiary?.name
        });
      }
    } catch (err) {
      console.log('Error fetching archetypes:', err);
    }
  };

  const handleGenerateGuidance = async () => {
    setGenerating(true);
    setError('');
    setSavedLogoDirection(null);  // Clear saved state when generating new

    try {
      const response = await fetch('/api/brand/generate-logo-direction', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setLogoGuidance(data.guidance);
        setArchetypeInfo(data.archetype_info);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate logo direction');
      }
    } catch (err) {
      setError('Error generating logo direction: ' + err.message);
    }

    setGenerating(false);
  };

  const handleSaveGuidance = async () => {
    if (!logoGuidance) return;

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/brand/save-logo-direction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ logo_direction: logoGuidance })
      });

      if (response.ok) {
        navigate('/dashboard/branding');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save logo direction');
      }
    } catch (err) {
      setError('Error saving logo direction: ' + err.message);
    }

    setSaving(false);
  };

  const fetchLogos = async () => {
    try {
      const response = await fetch('/api/brand/get-logos', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setLogos({
          horizontal: data.logo_horizontal,
          vertical: data.logo_vertical
        });
      }
    } catch (err) {
      console.log('Error fetching logos:', err);
    }
  };

  const hasBothLogos = logos.horizontal && logos.vertical;

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-4">🎯 Logo Direction & Style Guide</h1>

              {archetypeInfo && (
                <Alert variant="info" className="text-center mb-4">
                  <strong>Your Brand Archetypes:</strong> {archetypeInfo.primary}
                  {archetypeInfo.secondary && ` + ${archetypeInfo.secondary}`}
                  {archetypeInfo.tertiary && ` (with ${archetypeInfo.tertiary} accent)`}
                </Alert>
              )}

              {error && <Alert variant="danger">{error}</Alert>}

              {savedLogoDirection && logoGuidance && (
                <Alert variant="success" className="text-center mb-4">
                  <strong>✓ Your Saved Logo Direction</strong> - This is your logo guidance. You can regenerate to see new recommendations.
                </Alert>
              )}

              {!logoGuidance ? (
                <>
                  <div className="text-center py-5">
                    <p className="lead mb-4">
                      Generate AI-powered logo style guidance based on your brand archetype.
                    </p>
                    <p className="text-muted mb-4">
                      Claude will analyze your archetype and create comprehensive logo direction including
                      shape language, imagery style, design principles, and examples to inspire your logo design.
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleGenerateGuidance}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Generating Logo Direction...
                        </>
                      ) : (
                        'Generate Logo Direction →'
                      )}
                    </Button>
                  </div>

                  {/* Logo Upload Section - Link to upload page */}
                  <Card className="mb-4" style={{ borderColor: hasBothLogos ? '#28a745' : '#ffc107', borderWidth: '2px' }}>
                    <Card.Header style={{ backgroundColor: hasBothLogos ? '#d4edda' : '#fff3cd' }}>
                      <h5 className="mb-0">
                        {hasBothLogos ? '✓ Your Logos' : 'Upload Your Logos'}
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      {hasBothLogos ? (
                        <div className="d-flex gap-4 align-items-center mb-3">
                          <div className="text-center">
                            <small className="text-muted d-block mb-1">Horizontal</small>
                            <img src={logos.horizontal} alt="Horizontal logo" style={{ maxHeight: '60px', border: '1px solid #dee2e6', padding: '5px', backgroundColor: 'white' }} />
                          </div>
                          <div className="text-center">
                            <small className="text-muted d-block mb-1">Vertical</small>
                            <img src={logos.vertical} alt="Vertical logo" style={{ maxHeight: '80px', border: '1px solid #dee2e6', padding: '5px', backgroundColor: 'white' }} />
                          </div>
                        </div>
                      ) : (
                        <p className="mb-3">
                          Already have a logo? Upload both horizontal and vertical versions for use in your marketing materials.
                        </p>
                      )}
                      <Button
                        variant={hasBothLogos ? "outline-primary" : "primary"}
                        onClick={() => navigate('/dashboard/logo-upload')}
                      >
                        {hasBothLogos ? 'Manage Logos' : 'Upload Logos →'}
                      </Button>
                    </Card.Body>
                  </Card>
                </>
              ) : (
                <>
                  {generating && (
                    <Alert variant="info" className="text-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <strong>Generating fresh guidance...</strong> Claude is analyzing your brand archetype.
                    </Alert>
                  )}

                  <div style={{ opacity: generating ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                    {/* Overview */}
                    <Card className="mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                      <Card.Body>
                        <h4 className="mb-3">Logo Style Overview</h4>
                        <p className="lead mb-0">{logoGuidance.overview}</p>
                      </Card.Body>
                    </Card>

                    {/* Shape Language */}
                    <Card className="mb-4">
                      <Card.Header className="bg-primary text-white">
                        <h5 className="mb-0">Shape Language</h5>
                      </Card.Header>
                      <Card.Body>
                        <p><strong>Primary Shapes:</strong> {logoGuidance.shape_language.primary_shapes}</p>
                        <p><strong>Why:</strong> {logoGuidance.shape_language.reasoning}</p>
                        <p className="mb-0"><strong>Avoid:</strong> {logoGuidance.shape_language.avoid}</p>
                      </Card.Body>
                    </Card>

                    {/* Imagery Style */}
                    <Card className="mb-4">
                      <Card.Header className="bg-success text-white">
                        <h5 className="mb-0">Imagery Style</h5>
                      </Card.Header>
                      <Card.Body>
                        <p><strong>Style Direction:</strong> {logoGuidance.imagery_style.direction}</p>
                        <p><strong>Symbol Ideas:</strong> {logoGuidance.imagery_style.symbols}</p>
                        <p className="mb-0"><strong>Visual Metaphors:</strong> {logoGuidance.imagery_style.metaphors}</p>
                      </Card.Body>
                    </Card>

                    <Row>
                      <Col md={6}>
                        {/* Design Principles */}
                        <Card className="mb-4 h-100">
                          <Card.Header className="bg-warning">
                            <h5 className="mb-0">Design Principles</h5>
                          </Card.Header>
                          <Card.Body>
                            <ul className="mb-0">
                              {logoGuidance.design_principles.map((principle, idx) => (
                                <li key={idx} className="mb-2">{principle}</li>
                              ))}
                            </ul>
                          </Card.Body>
                        </Card>
                      </Col>

                      <Col md={6}>
                        {/* Color Approach */}
                        <Card className="mb-4 h-100">
                          <Card.Header className="bg-info text-white">
                            <h5 className="mb-0">Color Approach</h5>
                          </Card.Header>
                          <Card.Body>
                            <p><strong>Primary Colors:</strong> {logoGuidance.color_approach.primary}</p>
                            <p className="mb-0"><strong>Usage:</strong> {logoGuidance.color_approach.usage}</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    {/* Typography in Logo */}
                    <Card className="mb-4">
                      <Card.Header className="bg-secondary text-white">
                        <h5 className="mb-0">Typography in Logo</h5>
                      </Card.Header>
                      <Card.Body>
                        <p><strong>Wordmark Style:</strong> {logoGuidance.typography.wordmark_style}</p>
                        <p className="mb-0"><strong>Letter Treatment:</strong> {logoGuidance.typography.letter_treatment}</p>
                      </Card.Body>
                    </Card>

                    {/* Examples & Inspiration */}
                    <Card className="mb-4" style={{ backgroundColor: '#fffef0', border: '2px solid #f0e68c' }}>
                      <Card.Body>
                        <h5 className="mb-3">Brand Examples for Inspiration</h5>
                        <p className="mb-2"><strong>Similar Archetypes:</strong></p>
                        <p className="mb-3">{logoGuidance.examples.brands}</p>
                        <p className="mb-2"><strong>Why These Work:</strong></p>
                        <p className="mb-0">{logoGuidance.examples.reasoning}</p>
                      </Card.Body>
                    </Card>

                    {/* Implementation Notes */}
                    <Alert variant="secondary">
                      <h6>Implementation Notes</h6>
                      <p className="mb-0">{logoGuidance.implementation_notes}</p>
                    </Alert>
                  </div>

                  {/* Logo Upload Section - Link to upload page */}
                  <Card className="mb-4" style={{ borderColor: hasBothLogos ? '#28a745' : '#ffc107', borderWidth: '2px' }}>
                    <Card.Header style={{ backgroundColor: hasBothLogos ? '#d4edda' : '#fff3cd' }}>
                      <h5 className="mb-0">
                        {hasBothLogos ? '✓ Your Logos' : 'Upload Your Logos'}
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <p className="mb-3">
                        {hasBothLogos
                          ? 'Your logos are ready to use in marketing materials.'
                          : 'Once you have your logo created (based on the direction above), upload both horizontal and vertical versions.'}
                      </p>
                      {hasBothLogos && (
                        <div className="d-flex gap-4 align-items-center mb-3">
                          <div className="text-center">
                            <small className="text-muted d-block mb-1">Horizontal</small>
                            <img src={logos.horizontal} alt="Horizontal logo" style={{ maxHeight: '60px', border: '1px solid #dee2e6', padding: '5px', backgroundColor: 'white' }} />
                          </div>
                          <div className="text-center">
                            <small className="text-muted d-block mb-1">Vertical</small>
                            <img src={logos.vertical} alt="Vertical logo" style={{ maxHeight: '80px', border: '1px solid #dee2e6', padding: '5px', backgroundColor: 'white' }} />
                          </div>
                        </div>
                      )}
                      <Button
                        variant={hasBothLogos ? "outline-primary" : "primary"}
                        onClick={() => navigate('/dashboard/logo-upload')}
                      >
                        {hasBothLogos ? 'Manage Logos' : 'Upload Logos →'}
                      </Button>
                    </Card.Body>
                  </Card>

                  <div className="d-flex justify-content-between mt-4">
                    <Button
                      variant="outline-secondary"
                      onClick={handleGenerateGuidance}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Generating...
                        </>
                      ) : (
                        '🔄 Regenerate Guidance'
                      )}
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      disabled={saving}
                      onClick={handleSaveGuidance}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save & Continue →'
                      )}
                    </Button>
                  </div>
                </>
              )}

              {!logoGuidance && (
                <div className="text-center mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/branding')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default LogoDirection;
